import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { GqlTalentPhaseNote } from '@bloomays-lib/types.shared';
import { AnswersCard, EmptyState, Title } from '@bloomays-lib/ui.shared';
import mainBloomaysLogo from '../../assets/illustrations/mains-blue.png';
import emptyImage from '../../assets/illustrations/bulle.png';

type JobTalentNotesProps = {
  notes: GqlTalentPhaseNote[];
  generalEvaluation: string | null;
};

const JobTalentNotes = ({ notes, generalEvaluation }: JobTalentNotesProps) => {
  const { t } = useTranslation('talentBoard');

  return (
    <TalentNotesTab>
      <>
        <TitleContainer>
          <Title textTitle={t('talentJob.generalTamEvaluation')} variant="heading6" sx={{ textAlign: 'left' }} />
          <Separator />
        </TitleContainer>
        {generalEvaluation ? (
          <GeneralEvaluationContainer>
            <div dangerouslySetInnerHTML={{ __html: generalEvaluation }} />
          </GeneralEvaluationContainer>
        ) : (
          <EmptyStateContainer>
            <EmptyState image={emptyImage} text={t('talentJob.emptyProfile')} />
          </EmptyStateContainer>
        )}
      </>

      <TitleContainer>
        <Title textTitle={t('talentJob.notesHistory')} variant="heading6" sx={{ textAlign: 'left' }} />
        <Separator />
      </TitleContainer>
      {notes.length === 0 && (
        <EmptyStateContainer>
          <EmptyState image={mainBloomaysLogo} text={t('talentJob.emptyProfile')} />
        </EmptyStateContainer>
      )}
      {notes.length > 0 &&
        notes.map((note) => (
          <AnswersCard
            key={note.id}
            title={note.phaseId}
            answers={Object.entries(note.answers).map(([question, answer]) => ({
              question,
              answer,
            }))}
          />
        ))}
    </TalentNotesTab>
  );
};

const TalentNotesTab = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.neutral.white,
    gap: '0px',
    marginTop: '16px',
  },
}));

const TitleContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '0 16px',
  },
}));

const Separator = styled('div')(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.neutral[20],
}));

const GeneralEvaluationContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  padding: '16px',
  borderRadius: '8px',
  textAlign: 'left',
  fontSize: '14px',
  '& p': {
    margin: 0,
  },
  '& ul, & ol': {
    margin: 0,
    paddingLeft: theme.spacing(2),
  },
  '& li': {
    margin: 0,
  },
}));

const EmptyStateContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    marginTop: '60px',
  },
}));

export { JobTalentNotes };
export default JobTalentNotes;
